import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  deleteSideloadManifest,
  getExtensionAuditLog,
  getExtensionUsage,
  getExtensionsCatalog,
  getExtensionsMarketplace,
  getMarketplaceExtensionManifest,
  getSideloadManifest,
  getTenantExtensions,
  updateTenantExtensions,
  uploadSideloadManifest,
} from '../../api/extensions.api';
import { SAMPLE_SIDELOAD_MANIFEST } from '../../pages/studio/studioExtensionExamples';
import StudioV2ExtensionBuilder from './StudioV2ExtensionBuilder';
import StudioV2WorkspaceSkills from './StudioV2WorkspaceSkills';
import StudioV2ToolCatalog from './StudioV2ToolCatalog';

const PRIMARY_TABS = new Set(['plugins', 'skills', 'tools', 'advanced']);
const ADVANCED_TABS = new Set(['builder', 'marketplace', 'sideload', 'audit']);

function resolveInitialTabs(initialTab) {
  const raw = initialTab || 'plugins';
  if (raw === 'installed') return { tab: 'plugins', advanced: 'builder' };
  if (ADVANCED_TABS.has(raw)) return { tab: 'advanced', advanced: raw };
  if (PRIMARY_TABS.has(raw)) return { tab: raw, advanced: 'builder' };
  return { tab: 'plugins', advanced: 'builder' };
}

export default function StudioV2ExtensionsPanel({ compact = false, initialTab = 'plugins' }) {
  const initial = resolveInitialTabs(initialTab);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [allExtensions, setAllExtensions] = useState([]);
  const [enabled, setEnabled] = useState(new Set());
  const [sideloadExtensions, setSideloadExtensions] = useState([]);
  const [auditEntries, setAuditEntries] = useState([]);
  const [sideloadJson, setSideloadJson] = useState('');
  const [sideloadBusy, setSideloadBusy] = useState(false);
  const [tab, setTab] = useState(initial.tab);
  const [advancedTab, setAdvancedTab] = useState(initial.advanced);
  const [marketplace, setMarketplace] = useState([]);
  const [builderManifest, setBuilderManifest] = useState(null);
  const [builderLoading, setBuilderLoading] = useState(false);
  const [extensionUsage, setExtensionUsage] = useState({});
  const [catalogTools, setCatalogTools] = useState([]);
  const [catalogSkills, setCatalogSkills] = useState([]);
  const fileInputRef = useRef(null);

  const goAdvanced = (sub) => {
    setAdvancedTab(sub);
    setTab('advanced');
  };

  const load = async () => {
    setLoading(true);
    setError('');
    const [tenantRes, catalogRes, auditRes, marketRes, usageRes] = await Promise.all([
      getTenantExtensions(),
      getExtensionsCatalog(),
      getExtensionAuditLog(30),
      getExtensionsMarketplace(),
      getExtensionUsage(),
    ]);
    setLoading(false);
    if (tenantRes.error) {
      setError(tenantRes.error);
    }
    setAllExtensions(tenantRes.allExtensions || catalogRes.catalog?.allExtensions || []);
    const extList = tenantRes.allExtensions || catalogRes.catalog?.allExtensions || [];
    const initial = (tenantRes.enabledExtensions && tenantRes.enabledExtensions.length)
      ? tenantRes.enabledExtensions
      : extList.filter((e) => e.enabled).map((e) => e.id);
    setEnabled(new Set(initial));
    setSideloadExtensions(catalogRes.catalog?.sideloadExtensions || []);
    setCatalogTools(catalogRes.catalog?.tools || []);
    setCatalogSkills(catalogRes.catalog?.skills || []);
    setAuditEntries(auditRes.entries || []);
    setMarketplace(marketRes.listings || []);
    setExtensionUsage(usageRes.usage || {});
  };

  useEffect(() => {
    load();
  }, []);

  const refreshCatalog = async () => {
    const [tenantRes, catalogRes] = await Promise.all([getTenantExtensions(), getExtensionsCatalog()]);
    if (tenantRes.allExtensions) setAllExtensions(tenantRes.allExtensions);
    if (tenantRes.enabledExtensions?.length) setEnabled(new Set(tenantRes.enabledExtensions));
    if (catalogRes.catalog) {
      setSideloadExtensions(catalogRes.catalog.sideloadExtensions || []);
      setCatalogTools(catalogRes.catalog.tools || []);
      setCatalogSkills(catalogRes.catalog.skills || []);
    }
  };

  const toggle = (id) => {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onSave = async () => {
    setSaving(true);
    setMessage('');
    setError('');
    const res = await updateTenantExtensions([...enabled]);
    setSaving(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    setMessage('Extension settings saved.');
    setEnabled(new Set(res.enabledExtensions || []));
    const auditRes = await getExtensionAuditLog(30);
    setAuditEntries(auditRes.entries || []);
  };

  const onUploadSideload = async () => {
    setSideloadBusy(true);
    setError('');
    setMessage('');
    try {
      const manifest = JSON.parse(sideloadJson);
      const res = await uploadSideloadManifest(manifest);
      if (res.error) {
        setError(res.error);
        return;
      }
      setMessage(`Sideloaded extension ${manifest.id || res.manifestId}.`);
      setSideloadJson('');
      await load();
    } catch (e) {
      setError(e.message || 'Invalid JSON manifest');
    } finally {
      setSideloadBusy(false);
    }
  };

  const onFilePick = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setSideloadJson(text);
    event.target.value = '';
  };

  const onRemoveSideload = async (extensionId) => {
    setSideloadBusy(true);
    setError('');
    const res = await deleteSideloadManifest(extensionId);
    setSideloadBusy(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    setMessage(`Removed sideload ${extensionId}.`);
    if (builderManifest?.id === extensionId) setBuilderManifest(null);
    await load();
  };

  const onEditInBuilder = async (extensionId) => {
    setBuilderLoading(true);
    setError('');
    setMessage('');
    const res = await getSideloadManifest(extensionId);
    setBuilderLoading(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    setBuilderManifest(res.manifest);
    goAdvanced('builder');
  };

  const onImportJsonToBuilder = () => {
    setError('');
    try {
      const manifest = JSON.parse(sideloadJson);
      setBuilderManifest(manifest);
      goAdvanced('builder');
      setMessage('Loaded JSON into builder — review and sideload.');
    } catch (e) {
      setError(e.message || 'Invalid JSON manifest');
    }
  };

  const onCloneToBuilder = async (extensionId, name) => {
    setBuilderLoading(true);
    setError('');
    const res = await getMarketplaceExtensionManifest(extensionId);
    setBuilderLoading(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    const slug = extensionId.split('.').pop() || 'plugin';
    const clone = {
      ...res.manifest,
      id: `com.tenant.clone-${slug}`,
      name: `${name} (tenant copy)`,
      tenantPolicy: { ...(res.manifest.tenantPolicy || {}), defaultEnabled: false },
    };
    setBuilderManifest(clone);
    goAdvanced('builder');
    setMessage(`Cloned ${name} into builder — set a unique plugin id, then sideload.`);
  };

  const onBuilderUploaded = async (manifest) => {
    setBuilderManifest(manifest);
    await load();
    setTab('plugins');
    setMessage(`Sideloaded ${manifest.id}. Enable it below and Save.`);
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, py: 2,
      }}
      >
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">Loading extensions…</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success" data-testid="extensions-message">{message}</Alert>}
      {!compact && (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab value="plugins" label="Plugins" />
          <Tab value="skills" label="Skills" data-testid="extensions-tab-skills" />
          <Tab value="tools" label="Tools" />
          <Tab value="advanced" label="Advanced" data-testid="extensions-tab-builder" />
        </Tabs>
      )}

      {(compact || tab === 'plugins') && (
        <>
          {!compact && (
            <Typography variant="body2" color="text.secondary">
              Turn plugins on for this workspace, then Save.
            </Typography>
          )}
          {allExtensions.length === 0 && (
            <Alert severity="info">
              No plugins loaded. Open Help for setup steps.
            </Alert>
          )}
          <Stack spacing={1}>
            {allExtensions.map((ext) => {
              const isCore = ext.id === 'com.chatbot.core';
              const checked = isCore || ext.enabled || enabled.has(ext.id);
              const requiresPlan = ext.tenantPolicy?.requiresPlan;
              const toolCount = catalogTools.filter((t) => t.extensionId === ext.id).length;
              const skillCount = catalogSkills.filter((sk) => sk.extensionId === ext.id).length;
              return (
                <Card key={ext.id} variant="outlined" sx={{ borderRadius: 1.5 }}>
                  <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <ExtensionOutlinedIcon color="action" fontSize="small" />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography variant="subtitle2" fontWeight={700}>{ext.name}</Typography>
                          {isCore && <Chip size="small" color="success" label="Always on" sx={{ height: 22 }} />}
                          {requiresPlan && <Chip size="small" color="warning" label={`${requiresPlan}+`} sx={{ height: 22 }} />}
                        </Stack>
                        {ext.description && (
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {ext.description}
                          </Typography>
                        )}
                        {!isCore && (toolCount > 0 || skillCount > 0) && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            data-testid={`extension-contents-${ext.id}`}
                          >
                            {[
                              toolCount ? `${toolCount} tool${toolCount === 1 ? '' : 's'}` : null,
                              skillCount ? `${skillCount} skill${skillCount === 1 ? '' : 's'}` : null,
                            ].filter(Boolean).join(' · ')}
                          </Typography>
                        )}
                      </Box>
                      <Switch
                        checked={checked}
                        onChange={() => toggle(ext.id)}
                        disabled={isCore || saving}
                        inputProps={{ 'aria-label': `Enable ${ext.name}` }}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
          <Button variant="contained" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </>
      )}

      {!compact && tab === 'skills' && (
        <StudioV2WorkspaceSkills
          tools={catalogTools.filter((t) => t.source !== 'flow-node')}
          onChanged={refreshCatalog}
        />
      )}

      {!compact && tab === 'tools' && (
        <StudioV2ToolCatalog embedded />
      )}

      {!compact && tab === 'advanced' && (
        <Stack spacing={2}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={advancedTab}
            onChange={(_, v) => { if (v) setAdvancedTab(v); }}
            aria-label="Advanced sections"
          >
            <ToggleButton value="builder">Builder</ToggleButton>
            <ToggleButton value="marketplace">Marketplace</ToggleButton>
            <ToggleButton value="sideload">Sideload</ToggleButton>
            <ToggleButton value="audit">Audit</ToggleButton>
          </ToggleButtonGroup>

          {advancedTab === 'builder' && (
            builderLoading ? (
              <Stack direction="row" spacing={1} alignItems="center" py={2}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">Loading…</Typography>
              </Stack>
            ) : (
              <StudioV2ExtensionBuilder
                key={builderManifest?.id || 'new'}
                initialManifest={builderManifest}
                onUploaded={onBuilderUploaded}
                onStatus={(text) => {
                  if (text === null) return;
                  if (text) setMessage(text);
                }}
              />
            )
          )}

          {advancedTab === 'marketplace' && (
            <Stack spacing={1.25}>
              {marketplace.length === 0 && (
                <Alert severity="info">No marketplace listings yet.</Alert>
              )}
              {marketplace.map((ext) => (
                <Card key={ext.id} variant="outlined">
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <ExtensionOutlinedIcon color="action" sx={{ mt: 0.25 }} />
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography variant="subtitle2" fontWeight={700}>{ext.name}</Typography>
                          {ext.featured && <Chip size="small" color="primary" label="Featured" />}
                          {ext.installed && <Chip size="small" color="success" label="Installed" />}
                        </Stack>
                        {ext.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{ext.description}</Typography>
                        )}
                      </Box>
                      <Stack direction="row" spacing={0.5} flexShrink={0}>
                        {!ext.installed && ext.id !== 'com.chatbot.core' && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setEnabled((prev) => new Set([...prev, ext.id]));
                              setTab('plugins');
                              setMessage(`Added ${ext.name} — click Save to enable.`);
                            }}
                          >
                            Add
                          </Button>
                        )}
                        {ext.cloneable && (
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<BuildOutlinedIcon />}
                            onClick={() => onCloneToBuilder(ext.id, ext.name)}
                            disabled={builderLoading}
                          >
                            Clone
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}

          {advancedTab === 'sideload' && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Paste a plugin manifest, or use the{' '}
                <Button
                  size="small"
                  variant="text"
                  sx={{
                    p: 0, minWidth: 0, verticalAlign: 'baseline', textTransform: 'none',
                  }}
                  onClick={() => {
                    setBuilderManifest(null);
                    setAdvancedTab('builder');
                  }}
                >
                  visual builder
                </Button>
                .
              </Typography>
              <Stack spacing={1}>
                <TextField
                  multiline
                  minRows={4}
                  fullWidth
                  placeholder='{ "schemaVersion": "1.0", "id": "com.example.myext", ... }'
                  value={sideloadJson}
                  onChange={(e) => setSideloadJson(e.target.value)}
                  size="small"
                  inputProps={{ 'aria-label': 'Extension manifest JSON' }}
                />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={sideloadBusy}
                  >
                    Choose file
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setSideloadJson(JSON.stringify(SAMPLE_SIDELOAD_MANIFEST, null, 2))}
                    disabled={sideloadBusy}
                  >
                    Sample
                  </Button>
                  <input ref={fileInputRef} type="file" accept="application/json,.json" hidden onChange={onFilePick} />
                  <Button variant="contained" onClick={onUploadSideload} disabled={sideloadBusy || !sideloadJson.trim()}>
                    {sideloadBusy ? 'Uploading…' : 'Upload'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<BuildOutlinedIcon />}
                    onClick={onImportJsonToBuilder}
                    disabled={sideloadBusy || !sideloadJson.trim()}
                  >
                    Open in builder
                  </Button>
                </Stack>
              </Stack>
              {sideloadExtensions.length > 0 && (
                <Stack spacing={1} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700}>Uploaded plugins</Typography>
                  {sideloadExtensions.map((ext) => (
                    <Card key={ext.id} variant="outlined">
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={600}>{ext.name || ext.id}</Typography>
                            <Typography variant="caption" color="text.secondary">{ext.id}</Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => onEditInBuilder(ext.id)}
                            disabled={sideloadBusy || builderLoading}
                            aria-label={`Edit ${ext.name || ext.id} in builder`}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => onRemoveSideload(ext.id)}
                            disabled={sideloadBusy}
                            aria-label={`Remove ${ext.name || ext.id}`}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>
          )}

          {advancedTab === 'audit' && (
            <Box>
              {Object.keys(extensionUsage).length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                  {Object.entries(extensionUsage).map(([extId, counts]) => (
                    <Chip
                      key={extId}
                      size="small"
                      variant="outlined"
                      label={`${extId}: ${counts.skillRuns || 0} skills · ${counts.toolCalls || 0} tools`}
                    />
                  ))}
                </Stack>
              )}
              <Typography variant="caption" fontWeight={700} color="text.secondary">Recent events</Typography>
              {auditEntries.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>No events yet.</Typography>
              ) : (
                <Stack spacing={0.75} sx={{ mt: 1 }}>
                  {auditEntries.slice(0, 15).map((entry) => (
                    <Typography key={entry._id} variant="caption" color="text.secondary" component="div">
                      {new Date(entry.createdAt).toLocaleString()} · {entry.event}
                      {entry.extensionId ? ` · ${entry.extensionId}` : ''}
                      {entry.skillId ? ` · skill ${entry.skillId}` : ''}
                      {entry.toolId ? ` · tool ${entry.toolId}` : ''}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Box>
          )}
        </Stack>
      )}
    </Stack>
  );
}
